"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ClipboardList, Compass, Hammer, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ProcessStep {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  deliverables: string[];
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const steps: ProcessStep[] = [{
  number: "01",
  title: "Initial Consultation",
  subtitle: "Discovery & Vision Assessment",
  description: "We meet at your home to evaluate your current bathroom layout, discuss your daily routine, gather aesthetic preferences, and establish a clear budgetary framework.",
  deliverables: ["On-site measurements", "Functional needs review", "Budget range definition"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_consultation_measure.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_consultation_measure.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_consultation_measure.png-wvc-sizes",
    alt: "Contractor and homeowner reviewing bathroom floor plan and measuring vanity space"
  },
  icon: ClipboardList
}, {
  number: "02",
  title: "Design & Material Planning",
  subtitle: "3D Visualizations & Selections",
  description: "Our designers craft detailed layouts, plumbing fixture plans, and photorealistic 3D renderings while curating tiles, custom vanities, quartz surfaces, and finishes.",
  deliverables: ["Comprehensive 3D renderings", "Complete tile & fixture specifications", "Fixed-scope proposal"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_design_samples.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_design_samples.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_design_samples.png-wvc-sizes",
    alt: "Sample board with marble tile swatches, brushed brass hardware, and quartz countertop"
  },
  icon: Compass
}, {
  number: "03",
  title: "Permitting & Procurement",
  subtitle: "Approvals & Staging Materials",
  description: "We submit architectural drawings, obtain municipal building and plumbing permits, and pre-order every fixture to ensure all components arrive before tearing down a single wall.",
  deliverables: ["City building & plumbing permits", "Material shipment verifications", "Pre-construction schedule"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_permitting_plans.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_permitting_plans.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_permitting_plans.webp-wvc-sizes",
    alt: "Approved residential architectural blueprints with permit documentation"
  },
  icon: CheckCircle2
}, {
  number: "04",
  title: "Demolition & Construction",
  subtitle: "Precision Trades & Daily Care",
  description: "Our crew isolates dust, cleanly demos old surfaces, runs updated rough-in plumbing and electrical, installs waterproof shower systems, and lays artisan tile.",
  deliverables: ["Schluter shower waterproofing", "Rough-in plumbing & electrical inspections", "Daily site cleanups"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_installation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_installation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_tile_installation.png-wvc-sizes",
    alt: "Tile craftsman installing large format porcelain wall tiles in a custom walk-in shower"
  },
  icon: Hammer
}, {
  number: "05",
  title: "Final Walkthrough & Handover",
  subtitle: "Quality Assurance & Warranty",
  description: "We conduct a white-glove clean, test all plumbing valves, complete punch-list details, and tour the finished bathroom with you before handing over warranty packages.",
  deliverables: ["Detailed punch list sign-off", "Professional deep cleaning", "Workmanship warranty booklet"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_completed.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_completed.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_completed.png-wvc-sizes",
    alt: "Completed luxury bathroom featuring a freestanding soaking tub and dual white oak vanity"
  },
  icon: Sparkles
}];
export default function RemodelingProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="remodelingprocess" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-mono uppercase tracking-wider px-3 py-1">
            Step-By-Step Workflow
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-semibold tracking-tight text-foreground">
            Our 5-Step Bathroom Remodeling Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From the initial in-home discovery to your first evening in a tranquil new space, our proven framework keeps your project on schedule, strictly transparent, and cleanly executed.
          </p>
        </div>

        {/* Steps Timeline Grid */}
        <div className="space-y-12 md:space-y-16">
          {steps.map((step, index) => {
          const Icon = step.icon;
          const isReversed = index % 2 === 1;
          return <div key={step.number} data-index={index} className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                {/* Photo Column */}
                <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative group overflow-hidden rounded-2xl border border-border bg-card shadow-md">
                    <div className="aspect-[4/3] w-full overflow-hidden">
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-xs px-3.5 py-1.5 rounded-full border border-border/70 flex items-center gap-2 shadow-xs">
                      <Icon className="size-4 text-primary" />
                      <span className="text-xs font-mono font-semibold tracking-wide text-foreground">
                        Phase {step.number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content Column */}
                <div className={`lg:col-span-6 flex flex-col justify-center ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-3xl sm:text-4xl font-bold text-primary tracking-tight">
                      {step.number}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mt-1">
                    {step.subtitle}
                  </p>

                  <p className="mt-4 text-base text-foreground/80 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Key Deliverables Card */}
                  <Card className="mt-6 border border-border bg-card shadow-xs py-4">
                    <CardContent className="p-0 px-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2">
                        {step.deliverables.map((item, dIndex) => <li key={dIndex} data-index={dIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                            <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>;
        })}
        </div>

        {/* Closing Action Band */}
        <div className="mt-20 p-8 sm:p-10 rounded-2xl bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-center justify-between gap-6 shadow-lg">
          <div className="text-center sm:text-left">
            <h3 className="text-2xl sm:text-3xl font-serif font-medium tracking-tight">
              Ready to begin your bathroom transformation?
            </h3>
            <p className="mt-1 text-secondary-foreground/80 text-sm sm:text-base">
              Schedule your complimentary initial consultation with our renovation specialists today.
            </p>
          </div>
          <Button asChild size="lg" variant="default" className="shrink-0 font-medium px-6 shadow-sm">
            <Link to="/ContactRequestAnEstimatePage">
              Request an Estimate
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}